import React from "react";
import EmailIcon from "@mui/icons-material/Email";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import type { MRT_Row } from "material-react-table";
import type { RowActionsSlot } from "@/components/tables/rowActions/rowActionsSlot";
import type { RowActionDescriptor } from "@/components/tables/rowActions/RowActionItem";

type UserRow = MRT_Row<Record<string, any>>;

export interface UserRowActionHandlers {
  t: (key: string, options?: Record<string, unknown>) => string;
  inCompanyContext: boolean;
  sendRegistrationEmail?: (row: UserRow) => void;
  unblock?: (row: UserRow) => void;
  undelete?: (row: UserRow, reloadTable: () => void) => void;
  showMails: (row: UserRow) => void;
  move: (row: UserRow, clearSelection: () => void) => void;
  mailPreferences?: (row: UserRow) => void;
}

const fullName = (user: Record<string, any>) =>
  user.first_name + " " + user.last_name;

export const userRowActions =
  (handlers: UserRowActionHandlers): RowActionsSlot =>
  (row, { standardActions, requestReload, clearSelection }) => {
    const { t } = handlers;
    const user = row.original;

    if (handlers.inCompanyContext && user.is_deleted) {
      return [
        ...standardActions,
        {
          key: "undelete",
          icon: <RestoreFromTrashIcon />,
          label: t("undelete", { user: fullName(user) }),
          onClick: () => handlers.undelete?.(row, requestReload),
        },
      ];
    }

    const actions: RowActionDescriptor[] = [
      ...standardActions,
      {
        key: "sendRegistrationEmail",
        icon: <EmailIcon />,
        label: user.is_registered
          ? t("send_password_reset_email")
          : t("send_registration_email"),
        onClick: () => handlers.sendRegistrationEmail?.(row),
        disabled: user.is_analog,
      },
    ];

    if (handlers.inCompanyContext) {
      if (user.locked_out) {
        actions.push({
          key: "unblockUser",
          icon: <LockOpenIcon />,
          label: t("unblock_user"),
          onClick: () => handlers.unblock?.(row),
        });
      }
      actions.push(
        {
          key: "showMails",
          icon: <AllInboxIcon />,
          label: t("show_all_mails_sent_to_user", { user: fullName(user) }),
          onClick: () => handlers.showMails(row),
        },
        {
          key: "movePerson",
          icon: <PersonRemoveIcon />,
          label: t("move_customer_to_company", { user: fullName(user) }),
          onClick: () => handlers.move(row, clearSelection),
        }
      );
    }

    if (handlers.mailPreferences) {
      actions.push({
        key: "mailPreferences",
        icon: <EmailIcon />,
        label: t("mail_preferences_title"),
        onClick: () => handlers.mailPreferences?.(row),
      });
    }
    return actions;
  };
